import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { MatIcon } from '@angular/material/icon'
import { Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { lastValueFrom } from 'rxjs'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { ButtonComponent } from '../../components/button/button.component'
import {
  AuthService,
  MFA_SETTINGS_PATH,
  PasskeyClaimMissingError,
} from '../../services/auth.service'
import { PasskeyService } from '../../services/passkey/passkey.service'
import { WEBAUTHN_CLIENT } from '../../services/passkey/webauthn-client'
import {
  passkeyErrorMessage,
  passkeyAuthenticationError,
} from '../../services/passkey/passkey-error'
import { getTitle } from '../../util/accessibility'

@Component({
  selector: 'app-passkey-setup-page',
  standalone: true,
  imports: [MatIcon, ButtonComponent, ReactiveFormsModule],
  templateUrl: './passkey-setup-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './passkey-setup-page.component.scss',
})
export class PasskeySetupPageComponent implements OnInit {
  private auth = inject(Auth)
  private authService = inject(AuthService)
  private passkeys = inject(PasskeyService)
  private webauthn = inject(WEBAUTHN_CLIENT)
  private router = inject(Router)
  busy = false
  errorMessage = ''
  canRetry = true
  supported = this.webauthn.browserSupportsWebAuthn()
  displayName = new FormControl(
    `パスキー ${new Date().toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}`,
    {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/\S/),
        Validators.maxLength(64),
      ],
    },
  )
  form = new FormGroup({ displayName: this.displayName })
  constructor() {
    inject(Title).setTitle(getTitle('パスキーの登録'))
  }
  async ngOnInit(): Promise<void> {
    await this.authService.waitForAuthStateReady()
    if (!this.auth.currentUser) await this.router.navigate(['/login'])
  }
  async submit(): Promise<void> {
    if (
      this.busy ||
      !this.canRetry ||
      !this.supported ||
      this.displayName.invalid
    )
      return
    if (!this.auth.currentUser) {
      await this.router.navigate(['/login'])
      return
    }
    this.busy = true
    this.errorMessage = ''
    try {
      const options = await lastValueFrom(this.passkeys.beginRegistration())
      const response = await this.webauthn.startRegistration({
        optionsJSON: JSON.parse(options.options),
      })
      const result = await lastValueFrom(
        this.passkeys.finishRegistration(
          options.challengeId,
          JSON.stringify(response),
          this.displayName.value.trim(),
        ),
      )
      if (result.customToken) {
        try {
          await this.authService.signInWithCustomToken(result.customToken)
        } catch (error) {
          if (error instanceof PasskeyClaimMissingError) throw error
          this.authService.clearMfaEnrollmentOrigin()
          await this.router.navigate(['/auth/passkey-verify'])
          return
        }
      } else {
        // 追加登録は既存の MFA セッションを維持する。初回のトークン発行失敗時のみ検証へ進む。
        if (!(await this.authService.hasPasskeyClaim())) {
          this.authService.clearMfaEnrollmentOrigin()
          await this.router.navigate(['/auth/passkey-verify'])
          return
        }
      }
      this.authService.clearMfaEnrollmentOrigin()
      const returnUrl = this.authService.consumeReturnUrl() || '/home'
      this.authService.clearLoginState()
      await this.router.navigateByUrl(returnUrl)
    } catch (error) {
      const code = (error as { code?: string }).code
      if (code === 'RECENT_LOGIN_REQUIRED') {
        if (this.authService.isFromMfaSettings()) {
          await this.authService.startReauthentication({ type: 'add-passkey' })
        } else {
          await this.authService.signOutPreservingReturnUrl()
          this.authService.redirectToLogin({ reason: 'reauth' })
        }
        return
      }
      if (code === 'PASSKEY_NOT_ALLOWED') {
        this.errorMessage = passkeyErrorMessage(error)
        this.canRetry = false
        return
      }
      if (error instanceof PasskeyClaimMissingError) {
        this.canRetry = false
        return
      }
      const authenticationError = passkeyAuthenticationError(error)
      if (authenticationError) {
        this.canRetry = false
        if (authenticationError === 'MFA_REQUIRED') {
          this.errorMessage =
            '認証状態を確認できませんでした。ログインからやり直してください。'
        }
        // その他の認証エラーの遷移は ErrorLink が行う。
        return
      }
      this.errorMessage = passkeyErrorMessage(error)
    } finally {
      this.busy = false
    }
  }
  async back(): Promise<void> {
    const fromSettings = this.authService.isFromMfaSettings()
    this.authService.clearMfaEnrollmentOrigin()
    if (fromSettings && this.auth.currentUser) {
      this.authService.consumeReturnUrl()
      await this.router.navigate([MFA_SETTINGS_PATH])
    } else {
      await this.router.navigate(['/auth/mfa-select'])
    }
  }
}
